/**
 * Messages module — append-only family communication log. Newest is pinned at
 * the top and highlighted; every message shows author + timestamp. There is no
 * edit and no delete (enforced by the backend; the UI offers neither).
 */
import { useMemo, useState } from 'react';
import type { Message } from '@solandia/core';
import { appendMessageInputSchema, initials } from '@solandia/core';
import { useI18n } from '../../i18n';
import { useApi } from '../../api/ApiProvider';
import { useFamily } from '../../family/FamilyContext';
import { useAsync } from '../../hooks/useAsync';
import { makePeopleResolver } from '../../family/people';
import {
  Badge,
  EmptyState,
  ErrorState,
  SectionHeader,
  Spinner,
} from '../../components/ui';

export function MessagesModule() {
  const { t } = useI18n();
  const api = useApi();
  const { family, me } = useFamily();
  // Family mode = warm "Odkazy" with no court/append-only framing; separated =
  // "Denník správ" with the permanence banner. The underlying append-only
  // behavior (no edit/delete) is identical in both modes — only copy changes.
  const separated = family.mode === 'separated';

  const { data, loading, error, reload } = useAsync<Message[]>(async () => {
    const res = await api.listMessages(family.id);
    return res.items;
  }, [family.id]);

  // Newest first (pinned at top).
  const ordered = useMemo(
    () => [...(data ?? [])].sort((a, b) => b.createdAt.localeCompare(a.createdAt)),
    [data],
  );

  const people = useMemo(
    () => makePeopleResolver(me, t.expenses.you),
    [me, t.expenses.you],
  );

  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);

  async function send() {
    const parsed = appendMessageInputSchema.safeParse({ body: draft.trim() });
    if (!parsed.success) return;
    setSending(true);
    setSendError(null);
    try {
      await api.appendMessage(family.id, parsed.data);
      setDraft('');
      reload();
    } catch {
      setSendError(t.errors.generic);
    } finally {
      setSending(false);
    }
  }

  const title = separated ? t.messages.titleSeparated : t.messages.titleFamily;
  const lead = separated ? t.messages.leadSeparated : t.messages.leadFamily;
  const placeholder = separated
    ? t.messages.placeholderSeparated
    : t.messages.placeholder;

  return (
    <section>
      <SectionHeader title={title} subtitle={lead} />

      <div className="card" style={{ marginBottom: 16 }}>
        <label className="field-label" htmlFor="msg-draft">
          {title}
        </label>
        <textarea
          id="msg-draft"
          className="textarea"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder={placeholder}
          rows={3}
          style={{ marginTop: 6 }}
        />
        {sendError ? (
          <p className="field-error" role="alert">
            {sendError}
          </p>
        ) : null}
        <div className="row-between" style={{ marginTop: 8 }}>
          {separated ? (
            <span className="text-xs muted">{t.messages.appendOnlyNote}</span>
          ) : (
            <span />
          )}
          <button
            type="button"
            className="btn btn-primary btn-sm"
            onClick={send}
            disabled={sending || draft.trim().length === 0}
          >
            {sending ? t.common.saving : t.messages.send}
          </button>
        </div>
      </div>

      {loading ? <Spinner /> : null}
      {error ? <ErrorState message={t.errors.generic} onRetry={reload} /> : null}
      {ordered.length === 0 && !loading ? (
        <EmptyState
          emoji={separated ? '💬' : '📝'}
          title={separated ? t.messages.emptySeparated : t.messages.emptyFamily}
        />
      ) : null}

      <ul className="list">
        {ordered.map((m, idx) => (
          <li
            key={m.id}
            className={idx === 0 ? 'message is-newest' : 'message'}
          >
            <div className="message-meta">
              <span className="row" style={{ gap: 8 }}>
                <span className="avatar" aria-hidden="true">
                  {initials(people.label(m.authorId))}
                </span>
                <span className="message-author">{people.label(m.authorId)}</span>
                {idx === 0 ? <Badge tone="primary">{t.messages.newest}</Badge> : null}
              </span>
              <time className="text-xs muted" dateTime={m.createdAt}>
                {new Date(m.createdAt).toLocaleString('sk-SK')}
              </time>
            </div>
            <p className="message-body">{m.body}</p>
          </li>
        ))}
      </ul>
    </section>
  );
}
